import Fuse from 'fuse.js';
import {useEffect, useMemo, useRef, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {fetchProjectFolderTypeahead, fetchUserTypeahead} from 'omnibox/OmniboxDAO';
import type {ProjectFolderTypeaheadItem, UserTypeaheadItem} from 'omnibox/OmniboxDAO';
import {useTagSchemasListingQuery} from 'schemas/useTagSchemasListingQuery';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

export interface OmniboxFilter {
  kind: 'type' | 'schema' | 'project' | 'folder' | 'user';
  id: string;
  name: string;
  iconId?: string;
  projectName?: string;
  handle?: string;
  isSelf?: boolean;
}

// Each entry maps a ModelTypeConstants value to a backend OmniboxObjectType string (id)
// and a display name. The id must match the backend OmniboxObjectType enum value exactly.
const OMNIBOX_TYPE_DEFINITIONS: ReadonlyArray<{
  modelType: ModelTypeConstants;
  id: string;
  name: string;
}> = [
  // Organization
  {modelType: ModelTypeConstants.FOLDER, id: 'Folder', name: 'Project or Folder'},
  {modelType: ModelTypeConstants.STUDY, id: 'Study', name: 'Study'},
  // Entities
  {modelType: ModelTypeConstants.SEQUENCE, id: 'DNA Sequence', name: 'DNA Sequence'},
  {modelType: ModelTypeConstants.RNA_SEQUENCE, id: 'RNA Sequence', name: 'RNA Sequence'},
  {modelType: ModelTypeConstants.DNA_OLIGO, id: 'DNA Oligo', name: 'DNA Oligo'},
  {modelType: ModelTypeConstants.RNA_OLIGO, id: 'RNA Oligo', name: 'RNA Oligo'},
  {modelType: ModelTypeConstants.PROTEIN, id: 'Protein', name: 'Protein'},
  {modelType: ModelTypeConstants.BASIC_FOLDER_ITEM, id: 'Custom Entity', name: 'Custom Entity'},
  {modelType: ModelTypeConstants.MIXTURE, id: 'Mixture', name: 'Mixture'},
  {modelType: ModelTypeConstants.MOLECULE, id: 'Molecule', name: 'Molecule'},
  // Notebook
  {modelType: ModelTypeConstants.ENTRY, id: 'Entry', name: 'Entry'},
  {modelType: ModelTypeConstants.TEMPLATE_COLLECTION, id: 'Template Collection', name: 'Template Collection'},
  {modelType: ModelTypeConstants.PROTOCOL, id: 'Protocol', name: 'Protocol'},
  // Processes
  {modelType: ModelTypeConstants.PROCESS, id: 'Workflow', name: 'Workflow Task Group'},
  {modelType: ModelTypeConstants.PROCESS_TASK, id: 'Workflow Task', name: 'Workflow Task'},
  {modelType: ModelTypeConstants.PROCESS_OUTPUT, id: 'Workflow Output', name: 'Workflow Output'},
  {modelType: ModelTypeConstants.REQUEST_V2_SUBMISSION, id: 'Request', name: 'Request'},
  {modelType: ModelTypeConstants.ASSAY_RUN, id: 'Run', name: 'Run'},
  {modelType: ModelTypeConstants.BULK_ASSEMBLY, id: 'Assembly', name: 'Assembly'},
  // Data
  {modelType: ModelTypeConstants.DATASET, id: 'Dataset', name: 'Dataset'},
  {modelType: ModelTypeConstants.FILE, id: 'File', name: 'File'},
  // Inventory
  {modelType: ModelTypeConstants.CONTAINER, id: 'Container', name: 'Container'},
  {modelType: ModelTypeConstants.PLATE, id: 'Plate', name: 'Plate'},
  {modelType: ModelTypeConstants.BOX, id: 'Box', name: 'Box'},
  {modelType: ModelTypeConstants.LOCATION, id: 'Location', name: 'Location'},
  // Analytics
  {modelType: ModelTypeConstants.ANALYSIS, id: 'Analysis', name: 'Analysis'},
  {modelType: ModelTypeConstants.ANALYSIS_TEMPLATE, id: 'Analysis Template', name: 'Analysis Template'},
  {modelType: ModelTypeConstants.ANALYTICS_DASHBOARD, id: 'Dashboard', name: 'Dashboard'},
  {modelType: ModelTypeConstants.SEQUENCE_ANALYSIS, id: 'DNA/RNA Alignment', name: 'DNA/RNA Alignment'},
  {modelType: ModelTypeConstants.PROTEIN_ALIGNMENT, id: 'AA Alignment', name: 'AA Alignment'},
  // Other
  {modelType: ModelTypeConstants.CONNECTION, id: 'Connection', name: 'Connection'},
  {modelType: ModelTypeConstants.WORKLIST, id: 'Worklist', name: 'Worklist'},
  {modelType: ModelTypeConstants.WORKSHEET, id: 'Worksheet', name: 'Worksheet'},
];

function getSearchableTypes(): Array<{kind: 'type'; id: string; name: string}> {
  return OMNIBOX_TYPE_DEFINITIONS.filter((def) => isModelTypeEnabledForListing(def.modelType)).map((def) => ({
    kind: 'type' as const,
    id: def.id,
    name: def.name,
  }));
}

const FUSE_OPTIONS = {threshold: 0.3, findAllMatches: true, ignoreLocation: false};

export function createFuse<T>(items: Array<T>, keys: Array<string>): Fuse<T> {
  return new Fuse(items, {...FUSE_OPTIONS, keys});
}

export interface TypeaheadData {
  typeaheadItems: Array<OmniboxFilter>;
  projectFolderItems: Array<OmniboxFilter>;
  userItems: Array<OmniboxFilter>;
  fuse: Fuse<OmniboxFilter>;
  projectFuse: Fuse<OmniboxFilter>;
  userFuse: Fuse<OmniboxFilter>;
}

export default function useTypeaheadData(): TypeaheadData {
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const schemasData = useTagSchemasListingQuery();

  const [projectFolderItems, setProjectFolderItems] = useState<Array<OmniboxFilter>>([]);
  useEffect(() => {
    fetchProjectFolderTypeahead()
      .then((items) => {
        if (mountedRef.current) {
          setProjectFolderItems(
            items.map((item: ProjectFolderTypeaheadItem) => ({
              kind: item.kind,
              id: item.apiId,
              name: item.name,
              projectName: item.projectName,
            }))
          );
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch project/folder typeahead', e);
      });
  }, []);

  const [userItems, setUserItems] = useState<Array<OmniboxFilter>>([]);
  useEffect(() => {
    fetchUserTypeahead()
      .then((items) => {
        if (mountedRef.current) {
          setUserItems(
            items.map((item: UserTypeaheadItem) => ({
              kind: 'user' as const,
              id: item.apiId,
              name: item.name,
              handle: item.handle,
              isSelf: item.isSelf,
            }))
          );
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch user typeahead', e);
      });
  }, []);

  const searchableTypes = useMemo(() => getSearchableTypes(), []);

  const typeaheadItems = useMemo((): Array<OmniboxFilter> => {
    const items: Array<OmniboxFilter> = [...searchableTypes];
    if (schemasData) {
      const starredIds = new Set(schemasData.starredTagSchemaIds);
      const starred = schemasData.tagSchemas.filter((s) => starredIds.has(s.id));
      const rest = schemasData.tagSchemas.filter((s) => !starredIds.has(s.id));
      for (const s of [...starred, ...rest]) {
        items.push({kind: 'schema', id: s.id, name: s.name, iconId: s.iconId});
      }
    }
    return items;
  }, [schemasData, searchableTypes]);

  const fuse = useMemo(() => createFuse(typeaheadItems, ['name']), [typeaheadItems]);

  const projectFuse = useMemo(
    () => createFuse(projectFolderItems, ['name', 'projectName']),
    [projectFolderItems]
  );

  const userFuse = useMemo(() => createFuse(userItems, ['name', 'handle']), [userItems]);

  return {
    typeaheadItems,
    projectFolderItems,
    userItems,
    fuse,
    projectFuse,
    userFuse,
  };
}
